import React, { useCallback, useState } from "react";
import { CoreRequest } from "../../../../redux/config/serviceInstances";
import { SearchResultModuleGroupDto } from "./types";

const defaultState: SearchState = {
  error: false,
  loading: false,
  data: undefined,
  searchTerm: "",
};

type SearchState = {
  error: boolean;
  loading: boolean;
  data?: SearchResultModuleGroupDto[];
  searchTerm: string;
};

type UseSearchHook = {
  searchState: SearchState;
  handleSearch(value: string): void;
  clearSearch(): void;
};

export function useSearch(endpoint: string): UseSearchHook {
  const [searchState, setSearchState] = useState<SearchState>(defaultState);
  const search = useCallback(debounce(makeSearch, 300), []);

  function clearSearch() {
    setSearchState(defaultState);
  }

  function makeSearch(searchValue: string) {
    const params = new URLSearchParams({ phrase: searchValue });
    CoreRequest.get<SearchState["data"]>(endpoint, { params })
      .then((response) => {
        setSearchState((state) => ({
          ...state,
          data: response.data,
          loading: false,
          error: false,
        }));
      })
      .catch(() => {
        setSearchState((state) => ({
          ...state,
          data: undefined,
          loading: false,
          error: true,
        }));
      });
  }

  function handleSearch(searchTerm: string) {
    if (searchTerm.length >= 3) {
      setSearchState({
        data: undefined,
        error: false,
        loading: true,
        searchTerm,
      });
      search(searchTerm);
    } else {
      setSearchState({
        data: undefined,
        loading: false,
        error: false,
        searchTerm,
      });
    }
  }

  return { handleSearch, searchState, clearSearch };
}

/** Listener for tracking clicking outside an element.
 *
 * @description Returns a state and ref for attaching to element.
 */
import { useEffect } from "react";
import { debounce } from "../../../../utils/debounce";

export function useOnClickOutside<
  A extends React.MutableRefObject<HTMLElement | null>
>(refs: A | A[], handleClickOutside: (ev: MouseEvent) => void): void {
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const refsArr = Array.isArray(refs) ? refs : [refs];

      if (
        refsArr.every(
          (r) =>
            r.current &&
            r.current !== event.target &&
            !r.current?.contains(event.target as Node)
        )
      ) {
        handleClickOutside(event);
      }
    }

    document.body.addEventListener("click", handleClick, true);
    return () => document.body.removeEventListener("click", handleClick, true);
  }, [handleClickOutside, refs]);
}
